import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      try {
        // First try to get the session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (mounted) {
          if (error) {
            console.error("Session error:", error);
            setIsChecking(false);
            return;
          }
          
          if (!session) {
            // No session found
            setIsChecking(false);
            return;
          }
          
          // Verify the session is still valid
          const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
          
          if (userError || !currentUser) {
            console.error("User verification error:", userError);
            // Session exists but user verification failed - try to refresh
            const { data: { session: refreshedSession }, error: refreshError } = 
              await supabase.auth.refreshSession();
            
            if (refreshError || !refreshedSession) {
              // Refresh failed, clear any stale session data
              await supabase.auth.signOut();
              localStorage.clear();
            }
          }
          
          setIsChecking(false);
        }
      } catch (error) {
        console.error("Error checking session:", error);
        if (mounted) {
          setIsChecking(false);
        }
      }
    };

    checkSession();

    return () => {
      mounted = false;
    };
  }, [location.pathname, user]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // Store the attempted URL for redirect after login
    const from = location.pathname;
    return <Navigate to="/auth" state={{ from }} replace />;
  }

  return <>{children}</>;
}; 